import React, { useState, useEffect, useMemo, useCallback, forwardRef, useImperativeHandle } from 'react';
import { DatePicker, Select, Popover, message } from 'antd-v5';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQuery } from 'react-query';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import MessageComposer from '../../Routes/Configuration/Events/components/MessageComposer';
import MessagePreview from './MessagePreview';
import { generatePresignedUrl, uploadToS3 } from '../../../services/attachments';
import LargeAudienceConfirmModal from './LargeAudienceConfirmModal';
import { getRequest } from '../../../core/apiRequests';
import { TIMEZONES, DEFAULT_TEMPLATE_BUTTONS, defaultTimezone } from './constants';
import './MassMessageForm.scss';

const MAX_CHAR_LIMIT = 1500;
const EMOJI_CHAR_LIMIT = 670;
const EMOJI_REGEX = /[\p{Emoji_Presentation}\p{Extended_Pictographic}]/u;

dayjs.extend(utc);
dayjs.extend(timezone);

let attachmentIdCounter = 0;

const MassMessageForm = forwardRef(
  (
    {
      audienceCount = 0,
      audienceSection,
      templateButtons,
      enableAttachments = false,
      editingMessage,
      onSubmit,
      isSaving = false,
      renderActions,
      showCostAlways = false,
    },
    ref,
  ) => {
    const [messageText, setMessageText] = useState('');
    const [scheduledDate, setScheduledDate] = useState(null);
    const [tz, setTz] = useState(defaultTimezone);
    const [attachments, setAttachments] = useState([]);
    const [showLargeAudienceConfirm, setShowLargeAudienceConfirm] = useState(false);
    const [pendingFormData, setPendingFormData] = useState(null);

    // Populate fields on edit
    useEffect(() => {
      if (editingMessage) {
        setMessageText(editingMessage.message || '');
        setTz(editingMessage.timezone || defaultTimezone);
        if (editingMessage.scheduledDateTime) {
          setScheduledDate(dayjs(editingMessage.scheduledDateTime).tz(editingMessage.timezone || defaultTimezone));
        }
      }
    }, [editingMessage]);

    // Character count
    const charCount = useMemo(() => {
      const len = (messageText || '').length;
      let color = '#818181';
      if (len >= 600) color = '#FF7A7A';
      else if (len >= 300) color = '#FC8333';
      return { len, color };
    }, [messageText]);

    // Blacklisted words
    const { data: blackListWords } = useQuery(
      'black-list-words',
      async () => {
        const reqObj = { params: ['merchant', 'blacklistwords'], loadingStatusOff: true };
        const result = await getRequest(reqObj);
        // getRequest resolves to false on error
        return Array.isArray(result) ? result : [];
      },
      { refetchOnWindowFocus: false, initialData: [] },
    );

    const hasBlacklistedWord = useMemo(() => {
      if (!blackListWords?.length || !messageText) return false;
      const escaped = blackListWords.map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
      const regex = new RegExp(`\\b(${escaped.join('|')})\\b`, 'i');
      return regex.test(messageText);
    }, [blackListWords, messageText]);

    const hasEmojiWarning = useMemo(() => {
      const len = (messageText || '').length;
      return len > EMOJI_CHAR_LIMIT && EMOJI_REGEX.test(messageText);
    }, [messageText]);

    const isOverCharLimit = charCount.len > MAX_CHAR_LIMIT;

    const estimatedCost = (audienceCount * 0.07).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    // Attachment handling
    const handleAttach = useCallback(async files => {
      for (const file of files) {
        attachmentIdCounter += 1;
        const tempId = `temp-${attachmentIdCounter}`;
        const localUrl = URL.createObjectURL(file);

        setAttachments(prev => [...prev, { id: tempId, url: localUrl, uploading: true, fileSize: file.size }]);

        try {
          const { presignedUrl, fileUrl } = await generatePresignedUrl({ fileType: file.type });

          if (!presignedUrl) {
            throw new Error('Failed to get presigned URL');
          }

          await uploadToS3({ presignedUrl, file });

          setAttachments(prev =>
            prev.map(att => (att.id === tempId ? { ...att, url: fileUrl, uploading: false } : att)),
          );

          URL.revokeObjectURL(localUrl);
        } catch (err) {
          console.error('Upload failed:', err);
          message.error('Failed to upload image. Please try again.');
          setAttachments(prev => prev.filter(att => att.id !== tempId));
          URL.revokeObjectURL(localUrl);
        }
      }
    }, []);

    const handleRemoveAttachment = useCallback(id => {
      setAttachments(prev => prev.filter(att => att.id !== id));
    }, []);

    const attachmentsForComposer = useMemo(
      () =>
        attachments.map(att => ({
          ...att,
          onRemove: () => handleRemoveAttachment(att.id),
        })),
      [attachments, handleRemoveAttachment],
    );

    // Validation + submit
    const handleSubmit = useCallback(async () => {
      if (!messageText.trim()) {
        message.error('Please enter a message');
        return;
      }
      if (messageText.length > MAX_CHAR_LIMIT) {
        message.error(`Message exceeds the ${MAX_CHAR_LIMIT} character limit`);
        return;
      }
      if (hasBlacklistedWord) {
        message.error('Please remove blacklisted words before scheduling');
        return;
      }
      if (!scheduledDate) {
        message.error('Please select a date and time');
        return;
      }

      const isUploading = attachments.some(att => att.uploading);
      if (isUploading) {
        message.warning('Please wait for images to finish uploading');
        return;
      }

      const formData = {
        message: messageText,
        scheduledDateTime: scheduledDate.toISOString(),
        timezone: tz,
      };

      const attachmentUrls = attachments.map(att => att.url).filter(Boolean);
      if (attachmentUrls.length > 0) {
        formData.attachmentUrls = attachmentUrls;
      }

      setPendingFormData(formData);
      setShowLargeAudienceConfirm(true);
    }, [messageText, scheduledDate, tz, attachments, audienceCount, onSubmit, hasBlacklistedWord]);

    const handleLargeAudienceConfirm = useCallback(async () => {
      setShowLargeAudienceConfirm(false);
      if (pendingFormData) {
        await onSubmit(pendingFormData);
        setPendingFormData(null);
      }
    }, [pendingFormData, onSubmit]);

    const handleLargeAudienceCancel = useCallback(() => {
      setShowLargeAudienceConfirm(false);
      setPendingFormData(null);
    }, []);

    // Expose submit via ref
    useImperativeHandle(ref, () => ({ submit: handleSubmit }), [handleSubmit]);

    const buttons = templateButtons || DEFAULT_TEMPLATE_BUTTONS;
    const showCost = showCostAlways || audienceCount > 0;

    return (
      <div className="mass-message-form">
        {/* Render actions slot (modal header) */}
        {renderActions?.(handleSubmit, isSaving)}

        {/* Cost estimate */}
        {showCost && (
          <div className="mass-message-form__cost">This could cost ${estimatedCost} if you exceed your plan limit</div>
        )}

        {/* Audience section slot */}
        {audienceSection}

        {/* DELIVERY SCHEDULE */}
        <div className="mass-message-form__section">
          <h3 className="mass-message-form__section-title">
            <FontAwesomeIcon icon={['far', 'calendar-alt']} style={{ marginRight: 8 }} />
            DELIVERY SCHEDULE
          </h3>
          <p className="mass-message-form__tcpa-note">
            Messages only send between 9:00am and 7:00pm in your selected time zone. If a batch is queued outside that
            window, it will send the next day. This helps maintain TCPA compliance.
          </p>
          <div className="mass-message-form__schedule-row">
            <DatePicker
              showTime={{ format: 'hh:mm A', use12Hours: true }}
              format="MMM D, YYYY hh:mm A"
              value={scheduledDate}
              onChange={setScheduledDate}
              disabledDate={current => current && current < dayjs().startOf('day')}
              style={{ width: 220 }}
              placeholder="Select date and time"
            />
            <Select
              value={tz}
              onChange={setTz}
              options={TIMEZONES}
              style={{ width: 240 }}
              showSearch
              optionFilterProp="label"
              placeholder="Select a Timezone"
            />
          </div>
        </div>

        {/* MESSAGE */}
        <div className="mass-message-form__section">
          <h3 className="mass-message-form__section-title">
            <FontAwesomeIcon icon={['far', 'comment-alt-lines']} style={{ marginRight: 8 }} />
            MESSAGE
          </h3>
          <div className="mass-message-form__message-layout">
            <div className="mass-message-form__composer">
              <MessageComposer
                value={messageText}
                onChange={setMessageText}
                templateButtons={buttons}
                onAttach={enableAttachments ? handleAttach : undefined}
                attachments={enableAttachments ? attachmentsForComposer : undefined}
                footer={
                  <>
                    <div className="mass-message-form__char-count" style={{ color: charCount.color }}>
                      {charCount.len} characters
                    </div>
                    {(hasBlacklistedWord || hasEmojiWarning || isOverCharLimit) && (
                      <div className="mass-message-form__warnings">
                        {hasBlacklistedWord && (
                          <div className="mass-message-form__warning">
                            <ExclamationCircleOutlined />
                            <span>
                              You have used a{' '}
                              <Popover
                                content={
                                  <ul style={{ maxHeight: 200, overflowY: 'auto', margin: 0, paddingLeft: 20 }}>
                                    {blackListWords?.map(word => <li key={word}>{word}</li>)}
                                  </ul>
                                }
                                title="Blacklisted Words"
                              >
                                <span style={{ textDecoration: 'underline', cursor: 'pointer' }}>blacklisted</span>
                              </Popover>{' '}
                              word. Please remove it to send the message.
                            </span>
                          </div>
                        )}
                        {hasEmojiWarning && (
                          <div className="mass-message-form__warning">
                            <ExclamationCircleOutlined />
                            <span>
                              This message may not be{' '}
                              <Popover
                                content="If you include emojis, some carriers will not deliver messages over 670 characters."
                                title="Emoji Warning"
                              >
                                <span style={{ textDecoration: 'underline', cursor: 'pointer' }}>delivered</span>
                              </Popover>{' '}
                              to some carriers
                            </span>
                          </div>
                        )}
                        {isOverCharLimit && (
                          <div className="mass-message-form__warning">
                            <ExclamationCircleOutlined />
                            <span>Message exceeds the {MAX_CHAR_LIMIT} character limit and cannot be scheduled.</span>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                }
              />
            </div>
            <MessagePreview messageText={messageText} attachments={attachments} />
          </div>
          <div className="mass-message-form__guidelines">
            <div className="mass-message-form__guidelines-header">
              <FontAwesomeIcon icon={['far', 'lightbulb']} />
              Message Guidelines
            </div>
            <ul>
              <li>
                We recommend you keep mass messages under <strong>600 characters</strong> for optimal results.
              </li>
              <li>
                The maximum length of a message can be up to <strong>1,500 characters</strong>.
              </li>
              <li>
                If you include emojis, some carriers will not deliver messages over <strong>670 characters</strong>.
              </li>
              <li>
                You can add <strong>PNG, JPG, or GIF</strong> images.
              </li>
              <li>
                For the best phone display, use <strong>640&times;1138</strong> or <strong>1080&times;1920</strong>.
              </li>
              <li>
                The total size of all images together needs to be under <strong>1MB</strong> or they may not be
                delivered by some carriers.
              </li>
              <li>
                Any single image over <strong>600KB</strong> may not be delivered by some carriers.
              </li>
            </ul>
          </div>
        </div>
        <LargeAudienceConfirmModal
          open={showLargeAudienceConfirm}
          onCancel={handleLargeAudienceCancel}
          onConfirm={handleLargeAudienceConfirm}
          audienceCount={audienceCount}
          estimatedCost={estimatedCost}
        />
      </div>
    );
  },
);

MassMessageForm.displayName = 'MassMessageForm';

export default MassMessageForm;
